/**
 * One passkey on the passkeys page: its name (renamed in place), when it was
 * added and last used, whether it is synced, and a Remove action.
 */
import { Box, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { TbCloud, TbDeviceMobile, TbKey, TbPencil, TbTrash } from "react-icons/tb";

import Chip from "components/ds/Chip";
import Input from "components/ds/Input";
import PloyButton from "components/ds/PloyButton";
import UtilityButton from "components/ds/UtilityButton";
import { PASSKEY_LABEL_MAX } from "modules/members/webauthn";
import type { MFPasskey } from "state/members/passkeys";

import { addedLabel, lastUsedLabel, storageHint, storageLabel } from "./passkeyMeta";

export type PasskeyRowProps = {
  passkey: MFPasskey;
  now: number;
  /** Resolves when saved; rejects with a message to show. */
  onRename: (label: string) => Promise<void>;
  onRemove: () => void;
  isRemoving?: boolean;
};

const PasskeyRow = ({
  passkey,
  now,
  onRename,
  onRemove,
  isRemoving = false,
}: PasskeyRowProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(passkey.label);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEditing = () => {
    setDraft(passkey.label);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    const label = draft.trim();
    if (!label) {
      setError("Give your passkey a name.");
      return;
    }
    if (label === passkey.label) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onRename(label);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Flex
      gap="md"
      align="flex-start"
      px="lg"
      py="md"
      borderBottomWidth="1px"
      borderBottomColor="neutral.200"
      _last={{ borderBottomWidth: 0 }}
    >
      <Flex
        align="center"
        justify="center"
        boxSize="32px"
        borderRadius="full"
        bg="neutral.100"
        color="neutral.800"
        flexShrink={0}
      >
        <Icon as={TbKey} boxSize="16px" aria-hidden />
      </Flex>

      <Stack spacing="2xs" flex="1" minW={0}>
        {editing ? (
          <Stack spacing="1xs">
            <Input
              size="sm"
              aria-label="Passkey name"
              value={draft}
              maxLength={PASSKEY_LABEL_MAX}
              autoFocus
              isDisabled={saving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
            />
            {error && (
              <Text role="alert" fontSize="xs" color="error.700">
                {error}
              </Text>
            )}
            <Flex gap="sm">
              <PloyButton variant="primary" size="sm" onClick={save} isLoading={saving}>
                Save
              </PloyButton>
              <PloyButton
                variant="ghost"
                size="sm"
                onClick={() => setEditing(false)}
                isDisabled={saving}
              >
                Cancel
              </PloyButton>
            </Flex>
          </Stack>
        ) : (
          <Flex align="center" gap="sm" wrap="wrap">
            <Text fontSize="md" fontWeight={600} color="neutral.950" wordBreak="break-word">
              {passkey.label}
            </Text>
            <Box title={storageHint(passkey)}>
              <Chip
                tone="default"
                icon={
                  <Icon
                    as={passkey.backedUp ? TbCloud : TbDeviceMobile}
                    boxSize="12px"
                    aria-hidden
                  />
                }
              >
                {storageLabel(passkey)}
              </Chip>
            </Box>
          </Flex>
        )}
        <Text fontSize="smd" color="neutral.700">
          {addedLabel(passkey)} · {lastUsedLabel(passkey, now)}
        </Text>
      </Stack>

      {!editing && (
        <Flex gap="2xs" flexShrink={0}>
          <UtilityButton
            size="sm"
            treatment="subtle"
            sentiment="neutral"
            iconOnly
            aria-label={`Rename ${passkey.label}`}
            title="Rename"
            onClick={startEditing}
          >
            <Icon as={TbPencil} boxSize="14px" />
          </UtilityButton>
          <UtilityButton
            size="sm"
            treatment="subtle"
            sentiment="negative"
            iconOnly
            aria-label={`Remove ${passkey.label}`}
            title="Remove"
            isDisabled={isRemoving}
            onClick={onRemove}
          >
            <Icon as={TbTrash} boxSize="14px" />
          </UtilityButton>
        </Flex>
      )}
    </Flex>
  );
};

export default PasskeyRow;
